import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import MainLayout from './components/MainLayout';
import Home from './pages/Home';
import ScheduleRegister from './pages/ScheduleRegister';
import MyProfile from './pages/MyProfile';
import ScheduleManagement from './pages/ScheduleManagement';
import TutorManagement from './pages/TutorManagement';
import DataManagement from './pages/DataManagement';
import AdminManagement from './pages/AdminManagement';
import { expireTrialLessons } from './services/firestoreService';

import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 앱 초기화 시 로그인 상태 확인
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('사용자 정보 파싱 오류:', error);
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // 자동 체험수업 만료 처리 (매일 23:59:59)
  useEffect(() => {
    const checkAndExpireTrialLessons = async () => {
      try {
        const now = new Date();
        const currentTime = now.getHours() * 60 + now.getMinutes(); // 현재 시간을 분으로 변환
        const targetTime = 23 * 60 + 59; // 23:59를 분으로 변환
        
        // 23:59에 가까운 시간에만 실행 (1분 이내)
        if (Math.abs(currentTime - targetTime) <= 1) {
          console.log('🕐 자동 체험수업 만료 처리 시작...');
          await expireTrialLessons();
        }
      } catch (error) {
        console.error('자동 체험수업 만료 오류:', error);
      }
    };

    // 매 분마다 체크
    const interval = setInterval(checkAndExpireTrialLessons, 60000); // 1분 = 60000ms

    return () => clearInterval(interval);
  }, []);

  // 로그인 처리
  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  // 로그아웃 처리
  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  // 로딩 중일 때
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>로딩 중...</p>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route 
            path="/login" 
            element={
              user ? <Navigate to="/dashboard" replace /> : <Login onLogin={handleLogin} />
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              user ? (
                <MainLayout user={user} onLogout={handleLogout}>
                  <Home user={user} />
                </MainLayout>
              ) : <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/schedule/register" 
            element={
              user ? (
                <MainLayout user={user} onLogout={handleLogout}>
                  <ScheduleRegister user={user} />
                </MainLayout>
              ) : <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/profile" 
            element={
              user ? (
                <MainLayout user={user} onLogout={handleLogout}>
                  <MyProfile user={user} />
                </MainLayout>
              ) : <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/schedule/all" 
            element={
              user && user.role === 'admin' ? (
                <MainLayout user={user} onLogout={handleLogout}>
                  <ScheduleManagement user={user} />
                </MainLayout>
              ) : <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/tutor/management" 
            element={
              user && user.role === 'admin' ? (
                <MainLayout user={user} onLogout={handleLogout}>
                  <TutorManagement user={user} />
                </MainLayout>
              ) : <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/backup" 
            element={
              user && user.role === 'admin' ? (
                <MainLayout user={user} onLogout={handleLogout}>
                  <DataManagement user={user} />
                </MainLayout>
              ) : <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/admin/management" 
            element={
              user && user.role === 'admin' ? (
                <MainLayout user={user} onLogout={handleLogout}>
                  <AdminManagement user={user} />
                </MainLayout>
              ) : <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/stats" 
            element={<Navigate to="/dashboard" replace />} 
          />
          <Route 
            path="/" 
            element={<Navigate to={user ? "/dashboard" : "/login"} replace />} 
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
